import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Users, UserPlus, Trash2, ShieldCheck } from "lucide-react";
import { ShadowLogToggle } from "@/components/ShadowLog";
import { useCan } from "@/lib/permissions";

const MATRIX = [
  { perm: "Edit calendar",         head_coach: true, pitching_coach: true,  assistant_coach: false, athletic_trainer: false, strength_coach: false },
  { perm: "Edit weights",          head_coach: true, pitching_coach: true,  assistant_coach: false, athletic_trainer: false, strength_coach: true  },
  { perm: "Edit roster",           head_coach: true, pitching_coach: true,  assistant_coach: true,  athletic_trainer: true,  strength_coach: false },
  { perm: "View biometrics",       head_coach: true, pitching_coach: true,  assistant_coach: true,  athletic_trainer: true,  strength_coach: true  },
  { perm: "Manage staff",          head_coach: true, pitching_coach: true,  assistant_coach: false, athletic_trainer: false, strength_coach: false },
  { perm: "Override any decision", head_coach: true, pitching_coach: false, assistant_coach: false, athletic_trainer: false, strength_coach: false },
];

export default function StaffCard() {
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", team_role: "assistant_coach" });
  const [busy, setBusy] = useState(false);

  const myRole = user?.team_role || "pitching_coach";
  const isHead = useCan("manage_staff");

  const load = async () => {
    try {
      const { data } = await api.get("/coaches/staff");
      setStaff(data.staff || []);
      setRoles(data.roles || []);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!form.name || !form.email || !form.password) { toast.error("Name, email, password required"); return; }
    if (form.password.length < 8) { toast.error("Password must be 8+ characters"); return; }
    setBusy(true);
    try {
      await api.post("/coaches/staff", form);
      toast.success("Staff member added");
      setForm({ name: "", email: "", password: "", team_role: "assistant_coach" });
      setAdding(false);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const changeRole = async (uid, newRole) => {
    try {
      await api.patch(`/coaches/staff/${uid}/role`, { team_role: newRole });
      toast.success("Role updated");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (uid, name) => {
    if (!window.confirm(`Remove ${name}?`)) return;
    try {
      await api.delete(`/coaches/staff/${uid}`);
      toast.success("Staff member removed");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="rounded-2xl ink-card p-6" data-testid="staff-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Users className="h-5 w-5" style={{ color: "var(--brand-secondary)" }} />
          <div>
            <h2 className="font-display font-bold text-xl text-white">Coaching Staff</h2>
            <p className="text-xs text-white/50 mt-0.5">Head Coach + Pitching Coach share dual control. Head Coach has override authority.</p>
          </div>
        </div>
        {isHead && !adding && (
          <Button size="sm" onClick={() => setAdding(true)} className="text-white font-bold rounded-full red-glow" style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="staff-add-btn">
            <UserPlus className="h-4 w-4 mr-2" /> Add staff
          </Button>
        )}
      </div>

      {adding && (
        <div className="mt-5 rounded-xl border border-[var(--ink-border)] p-4 grid sm:grid-cols-2 gap-3" data-testid="staff-add-form">
          <div>
            <Label className="text-white/70 text-xs">Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[var(--ink-1)] border-[var(--ink-border)] text-white mt-1.5" data-testid="staff-name" />
          </div>
          <div>
            <Label className="text-white/70 text-xs">Email</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[var(--ink-1)] border-[var(--ink-border)] text-white mt-1.5" data-testid="staff-email" />
          </div>
          <div>
            <Label className="text-white/70 text-xs">Temporary password (8+ chars)</Label>
            <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-[var(--ink-1)] border-[var(--ink-border)] text-white mt-1.5" data-testid="staff-password" />
          </div>
          <div>
            <Label className="text-white/70 text-xs">Role</Label>
            <Select value={form.team_role} onValueChange={(v) => setForm({ ...form, team_role: v })}>
              <SelectTrigger className="bg-[var(--ink-1)] border-[var(--ink-border)] text-white mt-1.5" data-testid="staff-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>{roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2 flex gap-2 justify-end mt-1">
            <Button variant="outline" onClick={() => setAdding(false)} className="border-white/15 text-white bg-white/5 hover:bg-white/10">Cancel</Button>
            <Button onClick={invite} disabled={busy} className="text-white font-bold" style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="staff-invite-submit">
              {busy ? "Adding…" : "Add staff member"}
            </Button>
          </div>
        </div>
      )}

      <div className="mt-6 overflow-hidden rounded-xl border border-[var(--ink-border)]">
        <table className="w-full text-sm">
          <thead className="text-[10px] tracking-[0.2em] font-bold uppercase text-white/40" style={{ backgroundColor: "var(--ink-1)" }}>
            <tr><th className="px-4 py-2.5 text-left">Member</th><th className="px-4 py-2.5 text-left">Role</th><th className="px-4 py-2.5 text-right" /></tr>
          </thead>
          <tbody className="divide-y divide-[var(--ink-border)]">
            {staff.map((s) => (
              <tr key={s.user_id} data-testid={`staff-row-${s.user_id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-white">{s.name}{s.user_id === user?.user_id && <span className="text-[10px] text-white/40 ml-2">(you)</span>}</div>
                  <div className="text-xs text-white/40">{s.email}</div>
                </td>
                <td className="px-4 py-3">
                  {isHead ? (
                    <Select value={s.team_role} onValueChange={(v) => changeRole(s.user_id, v)}>
                      <SelectTrigger className="w-44 bg-[var(--ink-1)] border-[var(--ink-border)] text-white text-xs" data-testid={`staff-role-${s.user_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <span className="text-white/80">{s.team_role_label}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  {isHead && s.user_id !== user?.user_id && (
                    <Button variant="ghost" size="icon" onClick={() => remove(s.user_id, s.name)} className="text-white/50 hover:text-red-400" data-testid={`staff-remove-${s.user_id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Permissions matrix */}
      <ShadowLogToggle />
      <div className="mt-6">
        <div className="text-[10px] tracking-[0.3em] font-bold uppercase text-white/40 flex items-center gap-2"><ShieldCheck className="h-3 w-3" /> Role permissions</div>
        <div className="mt-2 overflow-x-auto rounded-xl border border-[var(--ink-border)]">
          <table className="w-full text-xs">
            <thead className="text-[10px] tracking-[0.15em] font-bold uppercase text-white/40" style={{ backgroundColor: "var(--ink-1)" }}>
              <tr>
                <th className="px-3 py-2 text-left">Permission</th>
                <th className="px-3 py-2 text-center">Head</th>
                <th className="px-3 py-2 text-center">Pitching</th>
                <th className="px-3 py-2 text-center">Assistant</th>
                <th className="px-3 py-2 text-center">Trainer</th>
                <th className="px-3 py-2 text-center">Strength</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--ink-border)] text-white/85">
              {MATRIX.map((r, i) => (
                <tr key={i}>
                  <td className="px-3 py-2">{r.perm}</td>
                  {["head_coach", "pitching_coach", "assistant_coach", "athletic_trainer", "strength_coach"].map((k) => (
                    <td key={k} className="px-3 py-2 text-center">{r[k] ? <span className="text-green-400">✓</span> : <span className="text-white/20">—</span>}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
